/**
 * Create the Renewal Input Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createRenewalInputAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'renewal_input_agent',
    name: 'Renewal Input Agent',
    description: 'Step 1: Collect report number for renewal',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger the AI to ask for the report number
    initialPrompt: 'Start the report renewal process. Welcome the user, call show_report_input to display the input field, and ask them to enter their report number.',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['report_found', {
          target: { agentId: 'renewal_vehicle_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'show_report_input') {
        console.log('[RenewalInputAgent] Showing report input UI');
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Report input UI is now visible. User can enter report number.',
          },
          // UI component will handle showing the input
          // Transition happens when report_found is called
        };
      }
      
      if (toolName === 'cancel_renewal') {
        console.log('[RenewalInputAgent] User cancelled renewal');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
        };
      }
      
      if (toolName === 'report_found') {
        const vehicleCount = args.vehicle_count || 0;
        console.log('[RenewalInputAgent] Report found with', vehicleCount, 'vehicles');
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: `Report found with ${vehicleCount} vehicles`,
          },
          triggersTransition: true,
          transitionTo: 'renewal_vehicle_agent',
        };
      }
      
      // Inline FAQ - answer questions without leaving renewal flow
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[RenewalInputAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then remind user to enter report number.',
          },
          // Stay in renewal flow - no transition
          triggersTransition: false,
        };
      }
      
      // Universal navigation: Switch to lost report search
      if (toolName === 'start_lost_report_search') {
        console.log('[RenewalInputAgent] Switching to lost report search');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
        };
      }
      
      // Universal navigation: Switch to accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[RenewalInputAgent] Switching to accident reporting');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Universal navigation: Switch to FAQ
      if (toolName === 'go_to_faq') {
        console.log('[RenewalInputAgent] Switching to FAQ');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[RenewalInputAgent] Activated - Step 1 of renewal');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[RenewalInputAgent] Deactivated');
    },
  };
}

export default createRenewalInputAgent;

